import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Card, Button, Form, Spinner, Alert, Badge } from 'react-bootstrap';
import { getSubdomainStatus, provisionSubdomain, reconcileSubdomain } from '../../services/adminApi';
import { useAuth } from '../../services/AuthContext';

// Provisioning lifecycle → Badge colour (mirrors the admin_subdomain.py state machine).
const STATUS_VARIANT = { active: 'success', validating: 'info', pending: 'secondary', failed: 'danger' };

// Self-service white-label subdomain provisioning (ADR-0032). Provision creates the Front Door custom
// domain + managed cert + DNS records (admin-api orchestrates); managed-cert validation is async, so this
// page polls `reconcile` until the tenant goes live.
function Subdomain({ tenantCode }) {
    const { can } = useAuth();
    const [status, setStatus] = useState(null);   // { subdomain, status, error, afd_custom_domain }
    const [host, setHost] = useState('');
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const pollRef = useRef(null);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const s = await getSubdomainStatus(tenantCode);
            setStatus(s);
            setHost(h => h || s.subdomain || '');
        } catch (e) {
            setError(e.response?.data?.detail || 'Failed to load subdomain status.');
        } finally {
            setLoading(false);
        }
    }, [tenantCode]);

    useEffect(() => { load(); }, [load]);

    // Poll `reconcile` while validating — managed-cert issuance completes over minutes.
    useEffect(() => {
        if (status?.status !== 'validating') return undefined;
        pollRef.current = setInterval(async () => {
            try { setStatus(await reconcileSubdomain(tenantCode)); } catch { /* transient — keep polling */ }
        }, 5000);
        return () => clearInterval(pollRef.current);
    }, [status?.status, tenantCode]);

    const doProvision = async () => {
        const h = host.trim().toLowerCase();
        if (!h) return;
        setBusy(true);
        setError(null);
        try {
            setStatus(await provisionSubdomain(tenantCode, h));
        } catch (e) {
            setError(e.response?.data?.detail || 'Provisioning failed.');
        } finally {
            setBusy(false);
        }
    };

    if (loading) {
        return <div className="page-content"><Spinner animation="border" size="sm" /> Loading…</div>;
    }

    const st = status?.status;
    return (
        <div className="page-content">
            <div className="page-header d-flex align-items-center gap-2">
                <h1 className="page-title mb-0">Domain</h1>
                <Badge bg={STATUS_VARIANT[st] || 'secondary'} style={{ fontSize: 11 }}>{st || 'not provisioned'}</Badge>
            </div>

            <p className="text-muted" style={{ fontSize: 13, maxWidth: 720 }}>
                The white-label host this tenant's advice portal is served at (ADR-0032). Provisioning creates the
                Front Door custom domain + managed certificate and the DNS records; validation is asynchronous, so
                this page polls until it goes live. All changes are audited.
            </p>

            {error && <Alert variant="danger" className="py-2" style={{ fontSize: 13 }}>{error}</Alert>}
            {st === 'failed' && status?.error &&
                <Alert variant="warning" className="py-2" style={{ fontSize: 13 }}>{status.error}</Alert>}

            <Card className="border-0 shadow-sm" style={{ maxWidth: 560 }}>
                <Card.Body>
                    <Form.Group className="mb-3">
                        <Form.Label style={{ fontSize: 13, fontWeight: 600 }}>Host</Form.Label>
                        <Form.Control size="sm" value={host} onChange={e => setHost(e.target.value)}
                            placeholder="acme.tst.mywaive.app" />
                        <Form.Text className="text-muted">
                            The fully-qualified host, or the tenant's own vanity domain.
                        </Form.Text>
                    </Form.Group>
                    <div className="d-flex gap-2 align-items-center">
                        <Button variant="primary" size="sm" onClick={doProvision}
                            disabled={busy || !host.trim() || !can.onboardTenant()}
                            title={can.onboardTenant() ? undefined : "Requires the waive-onboarding role"}>
                            {busy && <Spinner size="sm" animation="border" className="me-1" />}
                            {st === 'active' ? 'Re-provision' : 'Provision'}
                        </Button>
                        {st === 'validating' &&
                            <span className="text-muted" style={{ fontSize: 12 }}>
                                <Spinner size="sm" animation="border" className="me-1" /> Validating certificate…
                            </span>}
                        {st === 'active' &&
                            <span className="text-success" style={{ fontSize: 12 }}>✓ Live at {status.subdomain}</span>}
                    </div>
                </Card.Body>
            </Card>
        </div>
    );
}

export default Subdomain;
